import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  IconButton,
  useDisclosure,
} from '@chakra-ui/react'
import { DataTypeConfigUtils, ListDataTypeConfig } from '@helo/cms'
import { Assert, List, pipe } from '@helo/utils'
import { isEqual } from 'lodash'
import { useRef, useState } from 'react'
import SimpleBar from 'simplebar-react'

import { ControlsIcon } from '../../components/Icons/ControlsIcon'
import { TableStateEditor } from '../../components/TableStateEditor'
import { Tooltip } from '../../components/Tooltip/Tooltip'
import { useAppStore } from '../../store/appStore/appStore'
import { ColumnState, TableStateUtils } from './tablewidget.parts'

export interface TableStateEditorDrawerProps {
  value: ColumnState[]
  defaultValue: ColumnState[]
  onChange?: (value: ColumnState[]) => void
  listDatatypeConfig: ListDataTypeConfig
}

interface TableStateEditorDrawerContentProps
  extends TableStateEditorDrawerProps {
  onClose: () => void
}

const TableStateEditorDrawerContent = ({
  value,
  defaultValue: propsDefaultValue,
  onChange,
  listDatatypeConfig,
  onClose,
}: TableStateEditorDrawerContentProps) => {
  const hasRole = useAppStore(store => store.auth.hasRole)

  // Extract column configs from listDatatypeConfig and filter by role permissions
  const accessibleConfigs = pipe(
    listDatatypeConfig,
    TableStateUtils.getTableState(),
    ({ columnConfigs }) => columnConfigs,
    List.filter(columnConfig => hasRole(columnConfig.config?.state?.roles)),
  )

  // Filter the default value to only include columns that the user has access to
  const defaultValue = propsDefaultValue.filter(col =>
    accessibleConfigs.find(config => config.key === col.key),
  )

  // Filter the value prop to only include columns that the user has access to
  const filteredValue = value.filter(col =>
    accessibleConfigs.find(config => config.key === col.key),
  )

  const [tableState, setTableState] = useState<ColumnState[]>(filteredValue)

  // Extract objectDatatypeConfig from listDatatypeConfig.ofType
  const objectDataTypeConfig = pipe(
    listDatatypeConfig,
    DataTypeConfigUtils.getOfType(),
    Assert.orThrow(DataTypeConfigUtils.isKind('OBJECT')),
  )

  return (
    <>
      <DrawerOverlay />
      <DrawerContent data-testid="table-state-editor-drawer">
        <DrawerHeader
          display="flex"
          alignItems="center"
          gap="12px"
          px="16px"
          py="16px"
          pr="48px"
        >
          Manage Columns
          <Button
            variant="tertiary"
            data-testid="table-state-editor-reset-button"
            onClick={() => {
              onChange?.(defaultValue)
              onClose()
            }}
          >
            Reset to Default
          </Button>
        </DrawerHeader>
        <DrawerCloseButton />
        <DrawerBody px="16px" py="16px">
          <SimpleBar style={{ height: '100%' }}>
            <TableStateEditor
              columnConfigs={accessibleConfigs}
              objectDataTypeConfig={objectDataTypeConfig}
              value={tableState}
              onChange={setTableState}
            />
          </SimpleBar>
        </DrawerBody>
        <DrawerFooter p="16px">
          <Button
            data-testid="table-state-editor-apply-button"
            onClick={() => {
              onChange?.(tableState)
              onClose()
            }}
          >
            Apply
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </>
  )
}

export const TableStateEditorDrawer = ({
  value,
  defaultValue,
  onChange,
  listDatatypeConfig,
}: TableStateEditorDrawerProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const btnRef = useRef<HTMLButtonElement>(null)
  const isTableStateChanged = !isEqual(value, defaultValue)

  return (
    <>
      <Tooltip
        label={
          isTableStateChanged
            ? 'Manage Columns - changes applied'
            : 'Manage Columns'
        }
        placement="top"
      >
        <Box position="relative">
          <IconButton
            ref={btnRef}
            onClick={onOpen}
            variant="controlButton"
            aria-label="Refresh"
            data-testid="table-state-editor-open-button"
            fontSize="12px"
            icon={<ControlsIcon boxSize="16px" m="-2px" />}
          />
          {/* {isDefaultValueChanged && (
            <Box
              position="absolute"
              top="-4.5px"
              right="-4.5px"
              borderRadius="full"
              bgColor="presets.turquoise.40"
              width="9px"
              height="9px"
            />
          )} */}
        </Box>
      </Tooltip>
      <Drawer
        isOpen={isOpen}
        placement="right"
        onClose={onClose}
        finalFocusRef={btnRef}
        size="sm"
        // Avoid post-close transition timers that can fire after test teardown.
        // @ts-expect-error - motionPreset is not a valid prop for Drawer
        motionPreset="none"
      >
        {isOpen && (
          <TableStateEditorDrawerContent
            value={value}
            defaultValue={defaultValue}
            onChange={onChange}
            listDatatypeConfig={listDatatypeConfig}
            onClose={onClose}
          />
        )}
      </Drawer>
    </>
  )
}
